import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

/** Étapes LLM du pipeline de génération d'exercices (prompts paramétrables). */
export const PIPELINE_PROMPT_TYPES = [
  'scenarios_generate',
  'scenarios_audit',
  'scenarios_regenerate',
  'matching',
  'statement_generate',
  'statement_verify',
  'solution_generate',
  'solution_verify',
  'distractors_generate',
] as const;

export type PipelinePromptType = (typeof PIPELINE_PROMPT_TYPES)[number];

export type PromptConfigType = 'import_lesson' | 'solve_path' | PipelinePromptType;

/** Vrai si ce type utilise `user_prompt_template` (sinon `system_prompt`). */
export function usesUserTemplate(type: PromptConfigType): boolean {
  return type === 'import_lesson';
}

export type PromptConfig = {
  id: string;
  type: PromptConfigType;
  subject: SubjectSlug | null;
  level: SchoolLevel | null;
  system_prompt: string | null;
  user_prompt_template: string | null;
  prompt_version: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type UpsertPromptConfigInput = {
  id?: string;
  type: PromptConfigType;
  subject: SubjectSlug | null;
  level: SchoolLevel | null;
  system_prompt?: string | null;
  user_prompt_template?: string | null;
  prompt_version: string;
  is_active: boolean;
};

export function renderTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] ?? '');
}

export const IMPORT_LESSON_TEMPLATE_VARS = [
  'subject',
  'level',
  'title',
  'competences',
  'sources',
] as const;

// ai_prompt_configs n'est pas encore dans les types générés (migration à appliquer,
// puis pnpm db:gen-types). Les casts `any` seront retirés après la régénération.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export function usePromptConfigs() {
  return useQuery({
    queryKey: ['admin-prompt-configs'],
    queryFn: async (): Promise<PromptConfig[]> => {
      const { data, error } = await db
        .from('ai_prompt_configs')
        .select('*')
        .order('type')
        .order('subject', { nullsFirst: false })
        .order('level', { nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as PromptConfig[];
    },
  });
}

export function useUpsertPromptConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertPromptConfigInput) => {
      if (input.id) {
        const { error } = await db
          .from('ai_prompt_configs')
          .update({
            system_prompt: input.system_prompt ?? null,
            user_prompt_template: input.user_prompt_template ?? null,
            prompt_version: input.prompt_version,
            is_active: input.is_active,
          })
          .eq('id', input.id);
        if (error) throw error;
      } else {
        const { error } = await db.from('ai_prompt_configs').insert({
          type: input.type,
          subject: input.subject,
          level: input.level,
          system_prompt: input.system_prompt ?? null,
          user_prompt_template: input.user_prompt_template ?? null,
          prompt_version: input.prompt_version,
          is_active: input.is_active,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-prompt-configs'] }),
  });
}

/** Initialise en base les 9 prompts du pipeline (edge fn pipeline-sync-prompts). */
export function useSyncPipelinePrompts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ ok: boolean; count: number }> => {
      const { data, error } = await supabase.functions.invoke('pipeline-sync-prompts', {
        body: {},
      });
      if (error) throw error;
      return data as { ok: boolean; count: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-prompt-configs'] }),
  });
}

export function useDeletePromptConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from('ai_prompt_configs').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-prompt-configs'] }),
  });
}
